import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { SEO } from './SEO';
import { DeepSeekClient } from '../services/deepseekClient';
import '../styles/chat.css';

declare global {
  interface Window {
    gtag: (command: string, action: string, params: object) => void;
  }
}

const MAX_CHATS = 10;
const client = new DeepSeekClient();

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  isFollowUp?: boolean;
}

export const ChatInterface: React.FC = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [chatsRemaining, setChatsRemaining] = useState(MAX_CHATS);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (input.trim() === '' || isLoading) return;

    // Track chat event with global gtag
    window.gtag('event', 'send_message', {
      'event_category': 'Chat',
      'event_label': input.substring(0, 50) // First 50 characters of message
    });

    const isFollowUp = input.toLowerCase().includes('more detail') || 
                      input.toLowerCase().includes('detailed explanation') ||
                      input.toLowerCase().includes('tell me more');

    if (!isFollowUp && chatsRemaining <= 0) return;

    setIsLoading(true);
    const userMessage: ChatMessage = { 
      role: 'user', 
      content: input,
      isFollowUp 
    };
    setMessages(prev => [...prev, userMessage]);
    setInput('');

    try {
      const response = await client.getResponse(input);
      const assistantMessage: ChatMessage = { 
        role: 'assistant', 
        content: response,
        isFollowUp 
      };
      setMessages(prev => [...prev, assistantMessage]);
      
      if (!isFollowUp) {
        setChatsRemaining(prev => prev - 1);
      }
    } catch (error) {
      console.error('Failed to get response:', error);
      const errorMessage: ChatMessage = {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        isFollowUp
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <SEO 
        title="POE2 AI Build Assistant"
        description="Get personalized Path of Exile 2 build advice and recommendations from our AI assistant. Ask questions about skills, builds, and game mechanics."
        canonicalUrl="/chat"
      />
      <div className="chat-container">
        <div className="chat-status">
          <span className="chats-remaining">
            Remaining Chats: {chatsRemaining}/{MAX_CHATS}
          </span>
        </div>

        <div className="chat-messages">
          {messages.map((message, index) => (
            <div 
              key={index} 
              className={`message ${message.role} ${message.isFollowUp ? 'follow-up' : ''}`}
            >
              <ReactMarkdown>{message.content}</ReactMarkdown>
            </div>
          ))}
        </div>
        
        <form onSubmit={handleSubmit}>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={chatsRemaining > 0 ? "Ask about a PoE2 build..." : "Chat limit reached"}
            disabled={isLoading || (!input.toLowerCase().includes('more detail') && chatsRemaining <= 0)}
          />
          <button 
            type="submit" 
            disabled={isLoading || (!input.toLowerCase().includes('more detail') && chatsRemaining <= 0)}
          >
            {isLoading ? 'Sending...' : 'Send'}
          </button>
        </form>
      </div>
    </>
  );
};